import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { surveysQueryKeys } from '../Constants/queryKeys';
import { getSurveyResults } from '../Services/Surveys.service';
import type { SurveyResultsCut } from '../Types/Surveys.types';

/**
 * The aggregates of ONE survey, ONE version and — optionally — ONE cut.
 *
 * `version` is part of the key: results of two versions are answers written against different
 * wordings (R2), and caching them under the survey alone would print one under the other's
 * heading the moment the filter moved. The **cut** is in the key for the same reason, one level
 * down: with it on, `questions[]` counts a subgroup and everything else still counts everybody
 * (R49), so two payloads with the same version are still two different populations.
 *
 * The previous payload is kept on screen while the next one loads, so applying or clearing a cut
 * does not blank the whole screen and bring it back — the reader would lose the place they were
 * reading, and a screen that flashes empty invites a second click on the same control.
 */
export const useSurveyResults = (
  id?: string,
  version?: number,
  enabled = true,
  cut?: SurveyResultsCut,
) =>
  useQuery({
    queryKey: surveysQueryKeys.results(id ?? '', version, cut),
    queryFn: () => getSurveyResults(id ?? '', version, cut),
    enabled: Boolean(id) && enabled,
    placeholderData: keepPreviousData,
  });
